import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import ICollectionParams from '../../../../models/params/ICollectionParams';
import './Table.scss';

import { faMoneyBill, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Form } from 'react-bootstrap';
import { apiV2 } from '../../../../api/ApiV2';
import Loader from '../../../../components/common/Loader/Loader';
import Pagination from '../../../../components/common/Pagination/Pagination';
import { useUser } from '../../hooks/useUser';
import { pay } from '../OrdersFunctions';
import PaymentStatusBlock from '../payment-status-block/PaymentStatusBlock';
import { IOrderViewCollection } from 'models/collections/IOrderViewCollection';
import OrderActions from '../order-actions/OrderActions';
import OrdersFilter from '../orders-filter/OrdersFilter';
import IOrderViewCollectionFieldFilterParams from '../../../../models/params/IOrderViewCollectionFieldFilterParams';

interface ITableProps {}

const PageSizeVariants = [10, 20, 50, 100];
const defaultOrderCollectionSearchParams: IOrderViewCollection = {
  perPage: PageSizeVariants[0],
  pageNo: -1,
  sortColumn: 'Created',
  sortDirection: '-',
  totalCount: 0,
  itemsCount: 0,
  items: [],
  dataObjectParams: {
    _view_nested: true
  }
};

const Table: React.FunctionComponent<ITableProps> = () => {
  const [collectionParams, setCollectionParams] = useState<ICollectionParams>(
    defaultOrderCollectionSearchParams
  );
  const { getUser } = useUser();
  const user = getUser();
  const [selectedOrders, setSelectedOrders] = useState<number[]>([]);
  const [ordersFilterObject] = useState(
    {} as IOrderViewCollectionFieldFilterParams[]
  );

  const {
    data: ordersCollection = defaultOrderCollectionSearchParams,
    isLoading,
    refetch
  } = useQuery({
    queryKey: ['orders', collectionParams],
    queryFn: async () => {
      const response = await apiV2.order.getOrderViewCollection({
        collectionParams: collectionParams
      });
      return response.itemsCollection;
    }
  });

  const deleteOrders = async (orders: number[]) => {
    const params: ICollectionParams = {
      pageNo: collectionParams.pageNo,
      perPage: collectionParams.perPage
    };
    await apiV2.crud.delete({
      dataObjectName: 'MerchProductOrder',
      ids: orders,
      collectionParams: params
    });
    refetch();
  };

  const pageChange = (pageNo: number) => {
    setCollectionParams(prev => ({ ...prev, pageNo }));
  };

  const handleOrdersFilterObjectChange = (
    filters: IOrderViewCollectionFieldFilterParams[]
  ) => {
    setCollectionParams(prev => ({ ...prev, fieldFilters: filters }));
    return {};
  };

  return (
    <>
      <OrdersFilter
        ordersFilterObject={ordersFilterObject}
        setOrdersFilterObject={handleOrdersFilterObjectChange}
      />

      <div className="table-responsive orders-table">
        {isLoading && <Loader />}
        {!isLoading && (
          <div>
            <div className="row orders-header">
              <div className="col-md-6">
                <Pagination
                  {...ordersCollection}
                  pageChange={pageChange}
                  paginationCount={5}
                />
              </div>
              <div className="col-md-3 text-center">
                <span className="btn btn-sm">
                  {ordersCollection.totalCount} orders
                </span>
              </div>
              <div className="col-md-2 text-end"></div>
              <div className="col-md-1 text-end">
                <Form.Select
                  className="mb-4"
                  onChange={e =>
                    setCollectionParams(prev => ({
                      ...prev,
                      perPage: parseInt(e.target.value)
                    }))
                  }
                >
                  {PageSizeVariants.map(v => (
                    <option value={v} key={v}>
                      per page {v}
                    </option>
                  ))}
                </Form.Select>
              </div>
            </div>
            <hr />
            <table className="phoenix-table fs-9 table">
              <thead>
                <tr>
                  <th scope="col" className="align-middle">
                    <input
                      type="checkbox"
                      onChange={e => {
                        const checked = e.currentTarget.checked;
                        setSelectedOrders(
                          checked ? ordersCollection.items.map(o => o.ID) : []
                        );
                      }}
                    />
                  </th>
                  <th scope="col" className="tsort">
                    Name
                  </th>
                  <th scope="col" className="text-start sort">
                    External ID
                  </th>
                  <th scope="col" className="text-start sort">
                    Date
                  </th>
                  <th scope="col" className="text-end sort">
                    Total
                  </th>
                  <th scope="col" className="text-center">
                    Image
                  </th>
                  <th scope="col" className="text-center sort">
                    Payment status
                  </th>
                  <th scope="col" className="text-center sort">
                    Order status
                  </th>
                  <th scope="col" className="text-start sort">
                    Title
                  </th>
                  <th scope="col" className="text-start">
                    Tracking ID
                  </th>
                  <th scope="col" className="text-start">
                    Store
                  </th>
                  <th scope="col">
                    <div className="row">
                      <div className="col-2">
                        <span className="btn btn-sm order-action-btn">
                          Actions
                        </span>
                      </div>
                      <div className="col-10 text-end">
                        {user.IsAdmin && (
                          <button
                            className="btn btn-outline-danger btn-sm order-action-btn"
                            onClick={() =>
                              window.confirm('Really delete order?')
                                ? deleteOrders(selectedOrders)
                                : null
                            }
                          >
                            <FontAwesomeIcon
                              icon={faTrash}
                              className="ms-1 fs-9"
                            />
                            &nbsp; Delete
                          </button>
                        )}
                        <button
                          className="btn btn-outline-success btn-sm order-action-btn"
                          onClick={() =>
                            window.confirm('Really pay order?')
                              ? pay(selectedOrders)
                              : null
                          }
                        >
                          <FontAwesomeIcon
                            icon={faMoneyBill}
                            className="ms-1 fs-9"
                          />
                          &nbsp; Pay
                        </button>
                      </div>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {ordersCollection.items.map((o, idx) => {
                  return (
                    <tr key={idx}>
                      <td className="align-middle">
                        <input
                          type="checkbox"
                          data-id={o.ID}
                          checked={!!selectedOrders.find(or => or === o.ID)}
                          onChange={e => {
                            const checked = e.currentTarget.checked;
                            if (checked)
                              setSelectedOrders([o.ID, ...selectedOrders]);
                            else {
                              const ors = [...selectedOrders].filter(
                                ord => ord !== o.ID
                              );
                              setSelectedOrders(ors);
                            }
                          }}
                        />
                      </td>
                      <td className="text-start">{o.Name}</td>
                      <td className="text-start">
                        <span
                          title={o.ExternalID}
                          style={{
                            display: 'inline-block',
                            width: '160px',
                            overflow: 'hidden'
                          }}
                        >
                          {o.ExternalID}
                        </span>
                      </td>
                      <td className="text-start">{o.Created}</td>
                      {/* TODO: check if Total is correct price */}
                      <td className="text-end">{o.Total}</td>
                      <td className="text-center">
                        {/* TODO: get src and prod */}
                        {o.MockUpPublicUrl && (
                          <p className="mb-2">
                            <span
                              style={{
                                display: 'inline-block',
                                width: '60px',
                                height: '80px',
                                backgroundImage: `url("${o.MockUpPublicUrl}")`,
                                backgroundPosition: 'center center',
                                backgroundSize: 'cover',
                                backgroundRepeat: 'no-repeat'
                              }}
                            ></span>
                          </p>
                        )}
                      </td>
                      <td className="text-center">
                        <PaymentStatusBlock status={o.PaymentStatus} />
                      </td>
                      <td className="text-center">{o.OrderStatusTitle}</td>
                      <td className="text-start">{o.Title}</td>
                      <td className="text-start">{o.TrackingNumber}</td>
                      <td className="text-start">{o.StoreTitle}</td>
                      <td className="text-start">
                        <OrderActions order={o} updateFromServer={refetch} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

export default Table;
